import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, fmtCurrency, fmtDate } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/fintech/StatusPill';
import { toast } from 'sonner';

export default function DepositPage() {
  const { t } = useLanguage();
  const [sp] = useSearchParams();
  const [currency, setCurrency] = useState(sp.get('currency') || 'USD');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('bank_transfer');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/wallet/deposits').then(r => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/wallet/deposits', { currency, amount: Number(amount), method, notes });
      toast.success('Deposit request submitted');
      setAmount(''); setNotes('');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">{t('wallet.deposit')}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card className="bg-white"><CardContent className="p-6">
            <form onSubmit={submit} className="space-y-4" data-testid="deposit-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>{t('common.currency')}</Label><Select value={currency} onValueChange={setCurrency}><SelectTrigger data-testid="deposit-currency"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="KMF">KMF</SelectItem><SelectItem value="KZT">KZT</SelectItem></SelectContent></Select></div>
                <div><Label>{t('wallet.amount')}</Label><Input required type="number" step="0.01" min="0.01" data-testid="deposit-amount" value={amount} onChange={e => setAmount(e.target.value)} className="tabular-nums" /></div>
              </div>
              <div><Label>{t('wallet.method')}</Label><Select value={method} onValueChange={setMethod}><SelectTrigger data-testid="deposit-method"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="bank_transfer">{t('wallet.bank_transfer')}</SelectItem></SelectContent></Select></div>
              <div><Label>{t('wallet.notes')}</Label><Textarea rows={3} data-testid="deposit-notes" value={notes} onChange={e => setNotes(e.target.value)} /></div>
              <Button type="submit" disabled={busy} data-testid="deposit-submit" className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]">{t('wallet.submit_deposit')}</Button>
            </form>
          </CardContent></Card>
        </div>
        <div>
          <Card className="bg-white sticky top-24"><CardContent className="p-6">
            <div className="font-serif text-lg">Bank details</div>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Transfer to our correspondent account and submit this form. Include your Concorde reference in the payment purpose.</p>
            <div className="mt-4 space-y-2 text-sm">
              <div><span className="text-[hsl(var(--muted-foreground))]">Beneficiary:</span> Concorde Financial Limited</div>
              <div><span className="text-[hsl(var(--muted-foreground))]">Reference:</span> Please quote your user ID or email.</div>
              <div><span className="text-[hsl(var(--muted-foreground))]">Processing:</span> Same business day after receipt.</div>
            </div>
          </CardContent></Card>
        </div>
      </div>

      <Card className="bg-white overflow-hidden">
        <div className="p-4 border-b font-serif text-lg">{t('wallet.pending_requests')}</div>
        {items.length === 0 ? <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">{t('wallet.empty')}</div> : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--secondary))]"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Currency</th><th className="p-3 text-right">Amount</th><th className="p-3 text-left">Method</th><th className="p-3 text-left">Status</th></tr></thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-t">
                  <td className="p-3 text-xs">{fmtDate(it.created_at)}</td>
                  <td className="p-3">{it.currency}</td>
                  <td className="p-3 text-right tabular-nums">{fmtCurrency(it.amount, it.currency)}</td>
                  <td className="p-3">{it.method}</td>
                  <td className="p-3"><StatusPill status={it.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
